// ========================================
// MOCK API - FileSave Dashboard
// ========================================
// Simulação de APIs REST para desenvolvimento
// Simula delays de rede e estruturas de resposta reais

import {
    mockFiles,
    mockUsers,
    fileCategories,
    dashboardStats,
    mockNotifications,
    recentActivity,
    fileGroups,
    mockShares,
    mockUploadLinks,
    mockPlans
} from '@/data/mockData.js';

// ========================================
// UTILITÁRIOS
// ========================================

/**
 * Simula delay de rede para chamadas de API
 * @param {number} min - Delay mínimo em ms
 * @param {number} max - Delay máximo em ms
 */
const simulateNetworkDelay = (min = 800, max = 2000) => {
    const delay = Math.floor(Math.random() * (max - min + 1)) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Simula resposta de sucesso da API seguindo padrão do projeto
 * @param {any} data - Dados a serem retornados
 * @param {string} message - Mensagem de sucesso
 * @param {string} status - Status da operação
 */
const createSuccessResponse = (data, message = 'Operação realizada com sucesso', status = 'success') => ({
    data,
    status,
    message
});

/**
 * Simula resposta de erro da API seguindo padrão do projeto
 * @param {string} message - Mensagem de erro
 * @param {string} status - Status do erro
 */
const createErrorResponse = (message = 'Erro interno do servidor', status = 'error') => ({
    data: null,
    status,
    message
});

/**
 * Filtra arquivos por parâmetros
 * @param {Object} filters - Filtros a aplicar
 */
const filterDocuments = (filters = {}) => {
    let filtered = [...mockFiles];
    
    if (filters.status) {
        filtered = filtered.filter(doc => doc.status === filters.status);
    }
    
    if (filters.category) {
        filtered = filtered.filter(doc => doc.category === filters.category);
    }
    
    if (filters.type) {
        filtered = filtered.filter(doc => doc.type === filters.type);
    }
    
    if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(doc => 
            doc.name.toLowerCase().includes(searchLower) ||
            doc.type.toLowerCase().includes(searchLower) ||
            doc.documentNumber.toLowerCase().includes(searchLower)
        );
    }
    
    // Ordenação
    if (filters.sortBy) {
        filtered.sort((a, b) => {
            const aValue = a[filters.sortBy];
            const bValue = b[filters.sortBy];
            
            if (filters.sortOrder === 'desc') {
                return bValue > aValue ? 1 : -1;
            }
            return aValue > bValue ? 1 : -1;
        });
    }
    
    return filtered;
};

// ========================================
// API DE DOCUMENTOS
// ========================================

/**
 * Busca todos os arquivos com filtros opcionais
 * @param {Object} params - Parâmetros de busca
 */
export const fetchDocuments = async (params = {}) => {
    await simulateNetworkDelay();
    
    try {
        const {
            page = 1,
            limit = 10,
            ...filters
        } = params;
        
        const allDocuments = filterDocuments(filters);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const documents = allDocuments.slice(startIndex, endIndex);
        
        return createSuccessResponse({
            documents,
            pagination: {
                page: parseInt(page),
                limit: parseInt(limit),
                total: allDocuments.length,
                totalPages: Math.ceil(allDocuments.length / limit),
                hasNext: endIndex < allDocuments.length,
                hasPrev: page > 1
            }
        }, 'Arquivos carregados com sucesso');
        
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos');
    }
};

/**
 * Busca últimos arquivos adicionados
 * @param {number} limit - Quantidade de arquivos
 */
export const fetchRecentDocuments = async (limit = 8) => {
    await simulateNetworkDelay(500, 1200);
    
    try {
        const recentDocs = [...mockFiles]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, limit);
        
        return createSuccessResponse(recentDocs, 'Últimos arquivos carregados');
        
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos recentes');
    }
};

/**
 * Busca arquivo por ID
 * @param {number} documentId - ID do arquivo
 */
export const fetchDocumentById = async (documentId) => {
    await simulateNetworkDelay(300, 800);
    
    try {
        const document = mockFiles.find(doc => doc.id === parseInt(documentId));
        
        if (!document) {
            return createErrorResponse('Arquivo não encontrado', 404);
        }
        
        return createSuccessResponse(document, 'Arquivo encontrado');
        
    } catch (error) {
        return createErrorResponse('Erro ao buscar arquivo');
    }
};

/**
 * Simula upload de arquivo
 * @param {Object} documentData - Dados do arquivo
 */
export const uploadDocument = async (documentData) => {
    await simulateNetworkDelay(2000, 4000); // Upload demora mais
    
    try {
        const newDocument = {
            id: mockFiles.length + 1,
            ...documentData,
            createdAt: new Date().toISOString(),
            metadata: {
                uploadedBy: documentData.uploadedBy || 'Usuário Atual',
                lastModified: new Date().toISOString(),
                tags: documentData.tags || [],
                isPublic: false,
                hasNotifications: true
            }
        };
        
        // Simula falha de upload (5% de chance)
        if (Math.random() < 0.05) {
            return createErrorResponse('Falha no upload do arquivo', 500);
        }
        
        mockFiles.push(newDocument);
        
        return createSuccessResponse(newDocument, 'Arquivo enviado com sucesso');
        
    } catch (error) {
        return createErrorResponse('Erro no upload do arquivo');
    }
};

/**
 * Simula download de arquivo
 * @param {number} documentId - ID do arquivo
 */
export const downloadDocument = async (documentId) => {
    await simulateNetworkDelay(500, 1500);
    
    try {
        const document = mockFiles.find(doc => doc.id === parseInt(documentId));
        
        if (!document) {
            return createErrorResponse('Arquivo não encontrado para download', 404);
        }
        
        // Simula URL de download (seria gerada pelo backend)
        const downloadUrl = `https://filesave-storage.s3.amazonaws.com/documents/${document.id}/${document.name}.${document.fileFormat.toLowerCase()}`;
        
        return createSuccessResponse({
            downloadUrl,
            expiresIn: 300, // 5 minutos
            fileName: `${document.name}.${document.fileFormat.toLowerCase()}`
        }, 'Link de download gerado');
        
    } catch (error) {
        return createErrorResponse('Erro ao gerar download');
    }
};

/**
 * Remove arquivo
 * @param {number} documentId - ID do arquivo
 */
export const deleteDocument = async (documentId) => {
    await simulateNetworkDelay(800, 1200);
    
    try {
        const index = mockFiles.findIndex(doc => doc.id === parseInt(documentId));
        
        if (index === -1) {
            return createErrorResponse('Arquivo não encontrado', 404);
        }
        
        const deletedDocument = mockFiles.splice(index, 1)[0];
        
        return createSuccessResponse(deletedDocument, 'Arquivo removido com sucesso');
        
    } catch (error) {
        return createErrorResponse('Erro ao remover arquivo');
    }
};

/**
 * Busca arquivos agrupados por categoria
 */
export const fetchDocumentsByCategory = async (options = {}) => {
    await simulateNetworkDelay(600, 1500);
    
    try {
        const { limit = null } = options;
        const documentsByCategory = mockFiles.reduce((acc, doc) => {
            const category = doc.type || 'Sem Categoria';
            if (!acc[category]) {
                acc[category] = { 
                    documents: [],
                    expiring: 0,
                    expired: 0
                };
            }
            acc[category].documents.push(doc);

            if (doc.status === 'expiring') {
                acc[category].expiring++;
            }
            if (doc.status === 'expired') {
                acc[category].expired++;
            }

            return acc;
        }, {});
        
        let categories = Object.keys(documentsByCategory).map(categoryName => ({
            name: categoryName,
            documents: documentsByCategory[categoryName].documents,
            count: documentsByCategory[categoryName].documents.length,
            stats: {
                expiring: documentsByCategory[categoryName].expiring,
                expired: documentsByCategory[categoryName].expired
            }
        }));
        
        // Aplica limite se especificado
        if (limit && limit > 0) {
            categories = categories.slice(0, limit);
        }
        
        return createSuccessResponse(categories, 'Arquivos por categoria carregados');
        
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos por categoria');
    }
};

/**
 * Busca arquivos agrupados por tipo de arquivo
 */
export const fetchDocumentsByGroup = async (options = {}) => {
    await simulateNetworkDelay(600, 1500);
    
    try {
        const { limit = null } = options;
        
        let groups = [...fileGroups];
        
        // Aplica limite se especificado
        if (limit && limit > 0) {
            groups = groups.slice(0, limit);
        }
        
        return createSuccessResponse(groups, 'Arquivos por grupo carregados');
        
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos por grupo');
    }
};

// ========================================
// API DE CATEGORIAS
// ========================================

/**
 * Busca todas as categorias de arquivos
 */
export const fetchCategories = async () => {
    await simulateNetworkDelay(300, 600);
    
    try {
        return createSuccessResponse(fileCategories, 'Categorias carregadas');
    } catch (error) {
        return createErrorResponse('Erro ao carregar categorias');
    }
};

// ========================================
// API DE ESTATÍSTICAS
// ========================================

/**
 * Busca estatísticas do dashboard
 */
export const fetchDashboardStats = async () => {
    await simulateNetworkDelay(400, 900);
    
    try {
        // Calcula estatísticas em tempo real baseado nos dados mock
        const stats = {
            ...dashboardStats,
            totalDocuments: mockFiles.length,
            activeDocuments: mockFiles.filter(d => d.status === 'active').length,
            expiringDocuments: mockFiles.filter(d => d.status === 'expiring').length,
            expiredDocuments: mockFiles.filter(d => d.status === 'expired').length,
            recentUploads: mockFiles.filter(d => {
                const uploadDate = new Date(d.createdAt);
                const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                return uploadDate > weekAgo;
            }).length
        };
        
        return createSuccessResponse(stats, 'Estatísticas carregadas');
    } catch (error) {
        return createErrorResponse('Erro ao carregar estatísticas');
    }
};

// ========================================
// API DE NOTIFICAÇÕES
// ========================================

/**
 * Busca notificações do usuário
 * @param {Object} params - Parâmetros de busca
 */
export const fetchNotifications = async (params = {}) => {
    await simulateNetworkDelay(300, 700);
    
    try {
        const { unreadOnly = false, limit = 10 } = params;
        
        let notifications = [...mockNotifications];
        
        if (unreadOnly) {
            notifications = notifications.filter(n => !n.isRead);
        }
        
        notifications = notifications
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, limit);
        
        return createSuccessResponse(notifications, 'Notificações carregadas');
    } catch (error) {
        return createErrorResponse('Erro ao carregar notificações');
    }
};

/**
 * Marca notificação como lida
 * @param {number} notificationId - ID da notificação
 */
export const markNotificationAsRead = async (notificationId) => {
    await simulateNetworkDelay(200, 500);
    
    try {
        const notification = mockNotifications.find(n => n.id === parseInt(notificationId));
        
        if (!notification) {
            return createErrorResponse('Notificação não encontrada', 404);
        }
        
        notification.isRead = true;
        
        return createSuccessResponse(notification, 'Notificação marcada como lida');
    } catch (error) {
        return createErrorResponse('Erro ao marcar notificação');
    }
};

// ========================================
// API DE ATIVIDADES
// ========================================

/**
 * Busca atividades recentes
 * @param {number} limit - Limite de atividades
 */
export const fetchRecentActivity = async (limit = 5) => {
    await simulateNetworkDelay(400, 800);
    
    try {
        const activities = [...recentActivity]
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, limit);
        
        return createSuccessResponse(activities, 'Atividades carregadas');
    } catch (error) {
        return createErrorResponse('Erro ao carregar atividades');
    }
};

/**
 * Busca arquivos que estão vencendo nos próximos dias
 * @param {number} days - Número de dias para considerar como "vencendo"
 */
export const fetchExpiringDocuments = async (days = 30) => {
    await simulateNetworkDelay(400, 800);

    try {
        const now = new Date();
        const limitDate = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000));

        const expiringDocs = mockFiles
            .filter(doc => {
                if (!doc.expiryDate) return false;
                const expiryDate = new Date(doc.expiryDate);
                return expiryDate > now && expiryDate <= limitDate;
            })
            .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));

        return createSuccessResponse(expiringDocs, 'Arquivos vencendo carregados');
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos vencendo');
    }
};

/**
 * Busca documentos vencidos
 * @returns {Promise<Object>} Resposta com documentos vencidos
 */
export const fetchExpiredDocuments = async () => {
    await simulateNetworkDelay(400, 800);

    try {
        const now = new Date();

        const expiredDocs = mockFiles
            .filter(doc => {
                if (!doc.expiryDate) return false;
                const expiryDate = new Date(doc.expiryDate);
                return expiryDate < now;
            })
            .sort((a, b) => new Date(b.expiryDate) - new Date(a.expiryDate)); // Mais recente primeiro

        return createSuccessResponse(expiredDocs, 'Arquivos vencidos carregados');
    } catch (error) {
        return createErrorResponse('Erro ao carregar arquivos vencidos');
    }
};

// ========================================
// API DE USUÁRIOS (BÁSICA)
// ========================================

/**
 * Busca dados do usuário atual
 */
export const fetchCurrentUser = async () => {
    await simulateNetworkDelay(300, 600);
    
    try {
        // Simula usuário logado (normalmente viria do token/sessão)
        const currentUser = mockUsers[0]; // João Silva Santos
        
        return createSuccessResponse(currentUser, 'Dados do usuário carregados');
    } catch (error) {
        return createErrorResponse('Erro ao carregar usuário');
    }
};

// ========================================
// API DE COMPARTILHAMENTOS
// ========================================

/**
 * Busca todos os compartilhamentos do usuário
 */
export const fetchShares = async () => {
    await simulateNetworkDelay(800, 1500);

    try {
        return createSuccessResponse(mockShares, 'Compartilhamentos carregados');

    } catch (error) {
        return createErrorResponse('Erro ao carregar compartilhamentos');
    }
};

/**
 * Busca todos os links de upload de terceiros do usuário
 */
export const fetchUploadLinks = async () => {
    await simulateNetworkDelay(800, 1500);
    
    try {
        return createSuccessResponse(mockUploadLinks, 'Links de upload carregados');
        
    } catch (error) {
        return createErrorResponse('Erro ao carregar links de upload');
    }
};

// ========================================
// API DE PAGAMENTOS
// ========================================

/**
 * Valida cupom de desconto
 * @param {string} couponCode - Código do cupom
 */
export const validateCoupon = async (couponCode) => {
    await simulateNetworkDelay(500, 1200);
    
    try {
        const validCoupons = {
            'FILESAVE10': { discount: 10, type: 'percentage' },
            'WELCOME20': { discount: 20, type: 'percentage' },
            'SAVE50': { discount: 50, type: 'fixed' },
            'FIRSTMONTH': { discount: 100, type: 'percentage' }
        };
        
        const coupon = validCoupons[couponCode.toUpperCase()];
        
        if (!coupon) {
            return createErrorResponse('Cupom inválido ou não encontrado', 400);
        }
        
        // Simula cupom já usado (10% de chance)
        if (Math.random() < 0.1) {
            return createErrorResponse('Este cupom já foi utilizado', 400);
        }
        
        // Simula cupom expirado (5% de chance)
        if (Math.random() < 0.05) {
            return createErrorResponse('Este cupom está expirado', 400);
        }
        
        return createSuccessResponse({
            couponCode: couponCode.toUpperCase(),
            discount: coupon.discount,
            type: coupon.type,
            description: coupon.type === 'percentage' 
                ? `${coupon.discount}% de desconto`
                : `R$ ${coupon.discount.toFixed(2)} de desconto`,
            validUntil: '2025-12-31',
            minimumAmount: coupon.type === 'percentage' ? 0 : 100
        }, 'Cupom válido aplicado');
        
    } catch (error) {
        return createErrorResponse('Erro ao validar cupom');
    }
};

/**
 * Prepara dados de pagamento baseado na sessão
 * @param {string} sessionSlug - Slug da sessão de checkout
 * @param {Object} customerData - Dados do cliente
 * @param {string} paymentMethod - Método de pagamento
 * @param {Object} methodData - Dados específicos do método (cardData, pixData, etc)
 * @param {Object} coupon - Dados do cupom se aplicável
 */
export const preparePaymentData = async (sessionSlug, customerData, paymentMethod, methodData = {}, coupon = null) => {
	try {
		// Valida a sessão e obtém dados do plano
		const sessionResponse = await validateCheckoutSlug(sessionSlug);
		if (!sessionResponse.success) {
			return sessionResponse;
		}

		const planData = sessionResponse.data;

		// Prepara estrutura completa dos dados de pagamento
		const paymentData = {
			sessionSlug,
			plan: {
				id: planData.id,
				name: planData.name,
				price: planData.price,
				billing: planData.billing
			},
			customer: customerData,
			paymentMethod,
			methodData,
			coupon,
			timestamp: new Date().toISOString()
		};

		return createSuccessResponse(paymentData, 'Dados de pagamento preparados');

	} catch (error) {
		return createErrorResponse('Erro ao preparar dados de pagamento');
	}
};

/**
 * Processa pagamento com PagarMe
 * @param {Object} paymentData - Dados do pagamento
 */
export const processPayment = async (paymentData) => {
    await simulateNetworkDelay(2000, 4000); // Pagamento demora mais
    
    try {
        const {
            plan,
            customer,
            paymentMethod,
            methodData,
            coupon
        } = paymentData;
        
        // Simula falha de pagamento (8% de chance)
        if (Math.random() < 0.08) {
            const errors = [
                'Cartão recusado pela operadora',
                'Dados do cartão inválidos',
                'Limite insuficiente',
                'Cartão bloqueado',
                'Erro na conexão com o processador'
            ];
            return createErrorResponse(errors[Math.floor(Math.random() * errors.length)], 402);
        }
        
        // Simula diferentes tipos de resposta baseado no método de pagamento
        const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const baseResponse = {
            transactionId,
            plan: plan.name,
            amount: plan.price,
            discountAmount: coupon?.discount || 0,
            finalAmount: plan.price - (coupon?.discount || 0),
            customer: {
                name: customer.name,
                email: customer.email,
                document: customer.document
            },
            createdAt: new Date().toISOString(),
            status: 'processing'
        };
        
        switch (paymentMethod) {
            case 'credit_card':
                return createSuccessResponse({
                    ...baseResponse,
                    paymentMethod: 'Cartão de Crédito',
                    cardLastFour: methodData.number?.slice(-4) || '****',
                    cardBrand: getCardBrand(methodData.number || ''),
                    status: 'approved',
                    authorizationCode: `auth_${Math.random().toString(36).substr(2, 8)}`,
                    installments: methodData.installments || 1,
                    nextBilling: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                }, 'Pagamento aprovado com sucesso');
                
            case 'pix':
                return createSuccessResponse({
                    ...baseResponse,
                    paymentMethod: 'PIX',
                    pixQrCode: generatePixQrCode(),
                    pixCode: `00020126330014BR.GOV.BCB.PIX0111${Math.random().toString(36).substr(2, 11)}5204000053039865802BR5925FileSave Tecnologia Ltda6009SAO PAULO62070503***6304${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
                    expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutos
                    status: 'waiting_payment'
                }, 'PIX gerado com sucesso. Aguardando pagamento.');
                
            case 'boleto':
                return createSuccessResponse({
                    ...baseResponse,
                    paymentMethod: 'Boleto Bancário',
                    boletoUrl: `https://api.pagarme.com/boletos/${transactionId}.pdf`,
                    boletoBarcode: `34191.79001 01043.510047 91020.150008 ${Math.random().toString().substr(2, 1)} ${Math.random().toString().substr(2, 14)}`,
                    expiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 dias
                    status: 'waiting_payment'
                }, 'Boleto gerado com sucesso');
                
            default:
                return createErrorResponse('Método de pagamento não suportado', 400);
        }
        
    } catch (error) {
        return createErrorResponse('Erro interno no processamento do pagamento');
    }
};

/**
 * Utilitário para identificar bandeira do cartão
 * @param {string} cardNumber - Número do cartão
 */
const getCardBrand = (cardNumber) => {
    const number = cardNumber.replace(/\s/g, '');
    
    if (/^4/.test(number)) return 'Visa';
    if (/^5[1-5]/.test(number)) return 'Mastercard';
    if (/^3[47]/.test(number)) return 'American Express';
    if (/^6(?:011|5)/.test(number)) return 'Discover';
    if (/^(?:2131|1800|35)/.test(number)) return 'JCB';
    if (/^3(?:0[0-5]|[68])/.test(number)) return 'Diners Club';
    
    return 'Desconhecida';
};

/**
 * Gera QR Code simulado para PIX
 */
const generatePixQrCode = () => {
    // Em produção, seria gerado pelo PagarMe/Banco
    const baseUrl = 'data:image/png;base64,';
    const mockQrCode = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    return baseUrl + mockQrCode;
};

// ========================================
// API DE CHECKOUT SEGURO
// ========================================

// Armazena temporariamente os slugs gerados (em produção seria no banco)
const checkoutSessions = new Map();

/**
 * Gera slug seguro para checkout
 * @param {Object} planData - Dados básicos do plano (id, billing)
 */
export const generateCheckoutSlug = async (planData) => {
	await simulateNetworkDelay(300, 600);
	
	try {
		// Busca plano completo do mock
		const fullPlan = mockPlans.find(plan => plan.id === planData.id);
		if (!fullPlan) {
			return createErrorResponse('Plano não encontrado', 404);
		}

		// Gera slug único e seguro
		const timestamp = Date.now();
		const randomPart = Math.random().toString(36).substr(2, 8);
		const slug = `checkout_${timestamp}_${randomPart}`;
		
		// Calcula preços com desconto anual
		const calculatePricing = (originalPricing, billing, planDiscount) => {
			const monthlyPrice = originalPricing.monthly;
			const annualDiscount = planDiscount || 0; // Usa desconto do plano
			
			if (billing === 'monthly') {
				return {
					amount: monthlyPrice,
					originalAmount: monthlyPrice,
					discount: 0,
					discountPercentage: 0,
					billing: 'monthly',
					period: '/mês'
				};
			} else {
				// Anual: 12 meses com 15% de desconto
				const totalWithoutDiscount = monthlyPrice * 12;
				const discountAmount = totalWithoutDiscount * annualDiscount;
				const totalWithDiscount = totalWithoutDiscount - discountAmount;
				
				return {
					amount: totalWithDiscount,
					monthlyEquivalent: monthlyPrice,
					originalAmount: totalWithoutDiscount,
					discount: discountAmount,
					discountPercentage: Math.round(annualDiscount * 100),
					billing: 'yearly',
					period: '/ano'
				};
			}
		};

		const pricing = calculatePricing(fullPlan.price, planData.billing, fullPlan.annualDiscount);

		// Prepara dados completos do plano para a sessão
		const sessionData = {
			id: fullPlan.id,
			name: fullPlan.name,
			icon: fullPlan.icon,
			description: fullPlan.description,
			price: pricing.amount,
			pricing: pricing,
			billing: planData.billing,
			originalPrice: fullPlan.price,
			featured: fullPlan.featured,
			features: fullPlan.features,
			resources: planData.resources || {
				// Recursos padrão se não especificados
				storage: fullPlan.id === 'essential' ? 10 : 100,
				sharingPages: fullPlan.id === 'essential' ? 10 : 20,
				uploadPages: fullPlan.id === 'essential' ? 10 : 20,
				emailsSent: fullPlan.id === 'essential' ? 100 : 500,
				alerts: fullPlan.id === 'essential' ? 20 : 100
			},
			resourcesExtraCost: planData.resourcesExtraCost || 0,
			createdAt: new Date().toISOString(),
			expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutos
			isActive: true
		};
		
		checkoutSessions.set(slug, sessionData);
		
		return createSuccessResponse({
			slug,
			expiresIn: 30 * 60, // 30 minutos em segundos
			checkoutUrl: `/dashboard/checkout/${slug}`
		}, 'Sessão de checkout criada com sucesso');
		
	} catch (error) {
		return createErrorResponse('Erro ao criar sessão de checkout');
	}
};

/**
 * Valida slug e retorna dados do plano
 * @param {string} slug - Slug da sessão de checkout
 */
export const validateCheckoutSlug = async (slug) => {
	await simulateNetworkDelay(200, 500);
	
	try {
		const sessionData = checkoutSessions.get(slug);
		
		if (!sessionData) {
			return createErrorResponse('Sessão de checkout não encontrada', 404);
		}
		
		// Verifica se a sessão expirou
		const now = new Date();
		const expiresAt = new Date(sessionData.expiresAt);
		
		if (now > expiresAt || !sessionData.isActive) {
			checkoutSessions.delete(slug);
			return createErrorResponse('Sessão de checkout expirada', 410);
		}
		
		// Retorna dados do plano sem informações sensíveis
		const { createdAt, expiresAt: expires, isActive, ...planData } = sessionData;
		
		return createSuccessResponse({
			...planData,
			sessionInfo: {
				expiresAt: expires,
				remainingTime: Math.floor((expiresAt - now) / 1000) // segundos restantes
			}
		}, 'Dados do plano carregados');
		
	} catch (error) {
		return createErrorResponse('Erro ao validar sessão de checkout');
	}
};

/**
 * Invalida sessão de checkout após uso
 * @param {string} slug - Slug da sessão
 */
export const invalidateCheckoutSlug = async (slug) => {
	await simulateNetworkDelay(100, 300);

	try {
		const sessionData = checkoutSessions.get(slug);

		if (sessionData) {
			sessionData.isActive = false;
			checkoutSessions.set(slug, sessionData);
		}

		return createSuccessResponse(null, 'Sessão invalidada com sucesso');

	} catch (error) {
		return createErrorResponse('Erro ao invalidar sessão');
	}
};

// ========================================
// API DE PÁGINAS PÚBLICAS PROTEGIDAS
// ========================================

/**
 * Autentica usuário para páginas protegidas
 * @param {Object} credentials - Credenciais do usuário
 */
export const authenticatePublicPageUser = async (credentials) => {
    await simulateNetworkDelay(1200, 1800);

    try {
        // Mock de usuários válidos para páginas protegidas
        const validUsers = [
            { username: 'demo', password: '1234', role: 'viewer', name: 'Usuário Demo' },
            { username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
            { username: 'cliente', password: 'cliente2024', role: 'client', name: 'Cliente Empresarial' },
            { username: 'contador', password: 'cont123', role: 'accountant', name: 'Contador Responsável' }
        ];

        const user = validUsers.find(u =>
            u.username === credentials.username &&
            u.password === credentials.password
        );

        if (user) {
            return createSuccessResponse({
                user: {
                    username: user.username,
                    name: user.name,
                    role: user.role
                },
                token: `public_page_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                expiresIn: 3600
            }, 'Autenticação realizada com sucesso');
        } else {
            return createErrorResponse('Usuário ou senha incorretos', 401);
        }

    } catch (error) {
        return createErrorResponse('Erro interno na autenticação');
    }
};

/**
 * Busca documentos para páginas públicas (protegidas ou não)
 * @param {Object} params - Parâmetros da busca
 */
export const fetchPublicPageDocuments = async (params = {}) => {
    await simulateNetworkDelay(1000, 2500);

    try {
        const { isProtected = false, authToken = null } = params;

        // Se a página é protegida mas não tem token, retornar erro
        if (isProtected && !authToken) {
            return createErrorResponse('Token de autenticação necessário para acessar documentos protegidos', 401);
        }

        // Documentos públicos (para páginas não protegidas)
        const publicDocuments = [
            { id: 1, name: 'Informações Gerais da Empresa.pdf', size: '850 KB', type: 'PDF' },
            { id: 2, name: 'Manual do Cliente.docx', size: '1.2 MB', type: 'DOCX' },
            { id: 3, name: 'Termos de Uso.pdf', size: '320 KB', type: 'PDF' }
        ];

        // Documentos protegidos (mais sensíveis)
        const protectedDocuments = [
            { id: 1, name: 'Relatório Financeiro Anual 2024.pdf', size: '2.5 MB', type: 'PDF' },
            { id: 2, name: 'Proposta Comercial Confidencial.docx', size: '800 KB', type: 'DOCX' },
            { id: 3, name: 'Apresentação Executiva - Resultados.pptx', size: '12 MB', type: 'PPTX' },
            { id: 4, name: 'Contrato de Prestação de Serviços.pdf', size: '1.1 MB', type: 'PDF' },
            { id: 5, name: 'Planilha de Custos Operacionais.xlsx', size: '350 KB', type: 'XLSX' },
            { id: 6, name: 'Dados Confidenciais - Clientes VIP.xlsx', size: '445 KB', type: 'XLSX' },
            { id: 7, name: 'Estratégia de Mercado 2025.pdf', size: '1.8 MB', type: 'PDF' }
        ];

        const documents = isProtected ? protectedDocuments : publicDocuments;

        return createSuccessResponse({
            documents,
            total: documents.length,
            accessLevel: isProtected ? 'authenticated' : 'public',
            pageType: isProtected ? 'protected' : 'public'
        }, 'Documentos carregados com sucesso');

    } catch (error) {
        return createErrorResponse('Erro ao carregar documentos da página');
    }
};